import { computed, signal, untracked, WritableSignal } from '@angular/core';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { VisualFieldAllowed, VisualFieldNotAllowedReason } from './CanAddVisualFieldError';
import { Visual } from './Visual';
import { VisualField } from './VisualField';
import { VisualFieldRoleType, VisualFieldRoleTypeOrKind } from './VisualFieldRoleType';
import { VisualFieldTarget } from './VisualFieldTarget';
import { DataAggregation } from '../dataModel';

let nextRoleId = 1;

export class VisualFieldRole {
  readonly id = nextRoleId++;

  constructor(
    public readonly visual: Visual,
    public readonly type: VisualFieldRoleType) { }

  readonly fields = new VisualFieldRole.FieldCollection(this);

  get kind() { return this.type.kind; }
  get usage() { return `${VisualFieldRole.Usage.Prefix}${this.type.name}`; }

  get title() { return this.type.title; }
  get icon() { return this.type.icon; }
  get dropText() { return this.type.dropText }
  get description() { return this.type.description; }
  get displayOrder() { return this.type.displayOrder; }
  get itemStyle() { return this.type.itemStyle; }
  get maxDisplayedItems() { return this.type.maxDisplayedItems; }

  get usagePriority() { return this.type.usagePriority; }
  get allowsValues() { return this.type.allowsValues; }
  get allowsMeasures() { return this.type.allowsMeasures; }
  get allowsDuplicates() { return this.type.allowsDuplicates; }

  get limits() { return this.type.limits(this.visual); }

  readonly isRequired = computed(() => this.type.isRequired(this.visual));
  readonly isHidden = computed(() => this.type.isHidden(this.visual));
  readonly isEnabled = computed(() => !this.isHidden() && this.type.isEnabled(this.visual));
  
  readonly isValid = computed(() => {
    // If the role is 'disabled', it is also 'valid' because it cannot be used (and has no effect)
    if (!this.isEnabled())
      return true;

    if (this.isRequired()) {
      if (!this.fields.exludingInvalidCalc().length)
        return false;
    }

    let limits = this.limits;

    if ('min' in limits) {
      if (!isNullOrUndefined(limits.min) && this.fields.length < limits.min)
        return false;
    }

    if ('measures' in limits) {
      let measureLimits = limits.measures;
      let measureCount = this.fields.filter(f => f.isAggregated).length;
      if (!isNullOrUndefined(measureLimits.min) && measureCount < measureLimits.min)
        return false;
    }

    if ('groupings' in limits) {
      let groupingLimits = limits.groupings;
      let groupingCount = this.fields.filter(f => !f.isAggregated).length;
      if (!isNullOrUndefined(groupingLimits.min) && groupingCount < groupingLimits.min)
        return false;
    }

    return true;
  });

  fieldAllowed(field: VisualField | VisualFieldTarget): VisualFieldNotAllowedReason {
    let fieldTarget = (field instanceof VisualField) ? field.target : field;

    if (!this.isEnabled())
      return VisualFieldNotAllowedReason.DisabledRole;

    let roleFields = this.fields.value();

    if (!this.allowsDuplicates) {
      if (roleFields.some(f => f !== field && f.target.sameSource(fieldTarget)))
        return VisualFieldNotAllowedReason.DuplicateField;
    }

    let isMeasure = fieldTarget.isAggregated;
    let isValue = !isMeasure;
    let limits = this.limits;

    if (isMeasure) {
      if (!this.allowsMeasures)
        return VisualFieldNotAllowedReason.MeasuresNotAllowed;
    }

    if (isValue) {
      if (!this.allowsValues)
        return VisualFieldNotAllowedReason.ValuesNotAllowed;
    }
    
    let error = this.type.fieldAllowed(this.visual, fieldTarget);
    if (error)
      return error;

    if ('max' in limits) {
      if (!isNullOrUndefined(limits.max) && roleFields.length >= limits.max)
        return VisualFieldNotAllowedReason.LimitReached;
    }

    if ('measures' in limits) {
      let measureLimits = limits.measures;
      let measureCount = roleFields.filter(f => f.isAggregated).length;
      if (!isNullOrUndefined(measureLimits.max) && measureCount >= measureLimits.max)
        return VisualFieldNotAllowedReason.LimitReached;
    }

    if ('groupings' in limits) {
      let groupingLimits = limits.groupings;
      let groupingCount = roleFields.filter(f => !f.isAggregated).length;
      if (!isNullOrUndefined(groupingLimits.max) && groupingCount >= groupingLimits.max)
        return VisualFieldNotAllowedReason.LimitReached;
    }

    return VisualFieldAllowed;
  }

  aggregationAllowed(field: VisualField | VisualFieldTarget, aggr: DataAggregation) { 
    let fieldTarget = (field instanceof VisualField) ? field.target : field;
    return this.type.aggregationAllowed(this.visual, fieldTarget, aggr);
  }

  readonly sortable = computed(() => this.type.sortable(this.visual));

  readonly expansionState = signal(RoleItemGroupExpansionState.None);
}

export type VisualFieldRoleInput = VisualFieldRole | VisualFieldRoleTypeOrKind;

export namespace VisualFieldRole {
  export namespace Usage {
    export const Prefix = 'role:';

    export function tryParse(usage: string): string | null {
      usage = usage.toLowerCase();

      if (usage.startsWith(Prefix))
        return usage.substring(Prefix.length);

      return null;
    }
  }

  export class FieldCollection implements Iterable<VisualField> {
    readonly value = signal<ReadonlyArray<VisualField>>([]);

    readonly exludingInvalidCalc = computed(() => this.value().filter(f => !f.isInvalidCalculatedField()));

    constructor(public readonly role: VisualFieldRole) { }

    get length() {
      return this.value().length;
    }

    at(index: number, options?: { wrap?: boolean; }) {
      let value = this.value();

      if (options?.wrap)
        index = Math.max(Math.min(index, value.length - 1), 0);

      if (index < 0 || index >= value.length)
          return null;

      return value[index];
    }

    has(field: VisualField) {
      this.throwWhenFieldIsInvalid(field);

      return this.value().includes(field);
    }

    place(field: VisualField, index?: number) {
      untracked(() => {
        this.throwWhenFieldIsInvalid(field);

        let fieldRole = field.role();
        if (fieldRole) {
          if (fieldRole !== this.role)
            throw new Error(`Field is assigned to a different role`, { cause: field });

          this.value.update(x => {
            if (isNullOrUndefined(index) || index < 0)
              index = Math.max(x.length - 1, 0);

            let currentIndex = x.indexOf(field);
            if (currentIndex === index)
              return x;

            let list = [...x];
            list.splice(currentIndex, 1);

            if (currentIndex < index)
              index--;

            list.splice(index, 0, field);

            return list;
          });

          this.updateIndices();
        }
        else {
          this.insert(field, index);
        }
      });
    }

    add(field: VisualField) {
      untracked(() => {
        this.throwWhenFieldIsInvalid(field);
        this.expectHasNoRole(field);

        this.value.update(x => [...x, field]);
        setFieldRole(field, this.role);
        this.updateIndices();
      });
    }

    insert(field: VisualField, index?: number) {
      untracked(() => {
        this.throwWhenFieldIsInvalid(field);
        this.expectHasNoRole(field);

        this.value.update(x => {
          if (isNullOrUndefined(index))
            index = x.length;

          if (index < -1 || index > x.length)
            throw new Error(`Invalid index`, { cause: index });

          if (index === x.length)
            return [...x, field];
          else {
            let list = [...x];
            list.splice(index, 0, field);
            return list;
          }
        });

        setFieldRole(field, this.role);
        this.updateIndices();
      });
    }

    insertBefore(field: VisualField, position: VisualField) {
      untracked(() => {
        this.throwWhenFieldIsInvalid(position);
        let index = this.value().indexOf(position);
        this.insert(field, index);
      });
    }

    insertAfter(field: VisualField, position: VisualField) {
      untracked(() => {
        this.throwWhenFieldIsInvalid(position);
        let index = this.value().indexOf(position) + 1;
        this.insert(field, index);
      });
    }

    replace(field: VisualField, position: VisualField) {
      untracked(() => {
        this.throwWhenFieldIsInvalid(position);
        this.throwWhenFieldIsInvalid(field);

        this.expectHasRole(position);
        this.expectHasNoRole(field);

        this.value.update(x => x.map(f => (f === position) ? field : f));

        clearFieldRole(position);
        setFieldRole(field, this.role);
        this.updateIndices();
      });
    }

    remove(field: VisualField) {
      untracked(() => {
        this.expectHasRole(field);
        this.value.update(x => x.filter(f => f !== field));
        clearFieldRole(field);
        this.updateIndices();
      });
    }

    clear() {
      untracked(() => {
        let fields = this.value();
        this.value.set([]);
        fields.forEach(x => setFieldRole(x, null));
      });
    }

    filter(predicate: (field: VisualField, i?: number) => boolean) {
      return this.value().filter(predicate);
    }

    map<R>(transform: (field: VisualField, i?: number) => R) {
      return this.value().map(transform);
    }

    sort(callback: (a: VisualField, b: VisualField) => number) {
      return untracked(() => {
        let oldValue = this.value();
        let newValue = [...oldValue].sort(callback);
        if (newValue.some((x, i) => oldValue[i] !== x)) {
          this.value.set(newValue);
          this.updateIndices();
        }
      });
    }

    *[Symbol.iterator](): Iterator<VisualField> {
      for (let f of this.value())
        yield f;
    }

    private updateIndices() {
      this.value().forEach((x, i) => (x.roleIndex as WritableSignal<number>).set(i));
    }

    private throwWhenFieldIsInvalid(field: VisualField) {
      if (!field)
        throw new Error(`Field is null`, { cause: field });
    }

    private expectHasRole(field: VisualField) {
      let fieldRole = field.role();
      if (!fieldRole)
        throw new Error(`Field is not assigned to the role`, { cause: field });

      if (fieldRole !== this.role)
        throw new Error(`Field is assigned to a different role`, { cause: field });
    }

    private expectHasNoRole(field: VisualField) {
      let fieldRole = field.role();
      if (fieldRole)
        throw new Error(`Field is assigned to a role`, { cause: field });
    }
  }

  function clearFieldRole(field: VisualField) {
    (field.role as WritableSignal<VisualFieldRole>).set(null);
    (field.roleIndex as WritableSignal<number>).set(null);
  }

  function setFieldRole(field: VisualField, role: VisualFieldRole | null) {
    (field.role as WritableSignal<VisualFieldRole>).set(role);
  }
}

export enum RoleItemGroupExpansionState {
  None,
  Expanded,
  Collapsed
}
